<div class="container mx-auto p-4 relative">
  <!-- New Feature Flag Button -->
  <div class="flex justify-end items-center mb-4">
    <button
      type="button"
      (click)="toggleNewFeatureFlagForm()"
      class="w-8 h-8 bg-momentum-deep_purple text-white rounded-full hover:bg-momentum-royal_purple focus:outline-none focus:ring-2 focus:ring-momentum-deep_purple flex items-center justify-center"
    >
      <fa-icon [icon]="faPlus"></fa-icon>
    </button>
  </div>

  <!-- --------------------------------------- New Feature Flag Form ----------------------------------------------- -->
  @if (showNewFeatureFlagForm) {
    <div class="mb-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
      <h3 class="text-lg font-semibold text-agentium_gray-text mb-3">Add New Feature Flag</h3>
      <form [formGroup]="newFeatureFlagForm" (ngSubmit)="addNewFeatureFlag()">
        <div class="grid grid-rows-3 grid-cols-1 gap-4">
          <!-- Name -->
          <div>
            <label for="new-flag-name" class="block text-sm font-medium text-agentium_gray-text mb-1">Name</label>
            <input
              id="new-flag-name"
              type="text"
              [formControl]="getNewFormControl('name')"
              class="w-96 p-2 text-agentium_gray-text border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-momentum-deep_purple"
              placeholder="Enter feature flag name"
            />
            @if (isNewFormControlInvalid("name")) {
              <p class="text-red-500 mt-1">Name is required</p>
            }
          </div>

          <!-- Description -->
          <div>
            <label for="new-flag-description" class="block text-sm font-medium text-agentium_gray-text mb-1">Description</label>
            <textarea
              id="new-flag-description"
              [formControl]="getNewFormControl('description')"
              class="w-96 p-2 text-agentium_gray-text border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-momentum-deep_purple"
              rows="2"
              placeholder="Enter feature flag description"
            ></textarea>
            @if (isNewFormControlInvalid("description")) {
              <p class="text-red-500 mt-1">Description is required</p>
            }
          </div>

          <div class="flex flex-col justify-center">
            <!-- Enabled -->
            <div class="flex items-center">
              <label class="text-agentium_gray-text mr-4">Enabled</label>
              <div class="flex items-center">
                <input type="checkbox" [formControl]="getNewFormControl('enabled')" id="enabledToggle" class="sr-only" />
                @let enabled = newFeatureFlagForm.get("enabled")?.value;
                <label for="enabledToggle" class="flex items-center w-10 h-5 p-0.5 rounded-full cursor-pointer" [ngClass]="{ 'bg-momentum-deep_purple': enabled, 'bg-gray-300': !enabled }">
                  <span class="h-4 w-4 bg-white rounded-full shadow-md transform duration-200 ease-in-out" [ngClass]="{ 'translate-x-5': enabled }"></span>
                </label>
              </div>
            </div>

            <!-- Buttons -->
            <div class="mt-4 flex space-x-2">
              <button
                type="submit"
                [disabled]="!newFeatureFlagForm.dirty || newFeatureFlagForm.invalid || dataSource.isLoading"
                class="px-6 py-2 bg-momentum-deep_purple enabled:text-white rounded-full flex items-center disabled:bg-agentium_gray-text disabled:cursor-not-allowed"
              >
                Submit
              </button>
              <button
                type="button"
                (click)="toggleNewFeatureFlagForm()"
                class="px-6 py-2 bg-agentium_white-backdrop border border-gray-300 rounded-full disabled:bg-agentium_gray-text disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  }

  <!-- ---------------------------------------- Feature Flags Table ------------------------------------------------ -->
  <table cdk-table [dataSource]="dataSource" class="w-full">
    <!-- Name Column -->
    <ng-container cdkColumnDef="name">
      <th cdk-header-cell *cdkHeaderCellDef class="border-b p-2 min-w-[250px] text-agentium_gray-text">Name</th>
      <td cdk-cell *cdkCellDef="let featureFlag" class="border-b p-2 min-w-[250px] h-full">
        <div class="flex items-center">
          @if (!featureFlag.editMode) {
            <span class="text-agentium_gray-text pl-2">{{ featureFlag.name }}</span>
          } @else {
            <div class="w-full pl-2">
              <input
                type="text"
                [formControl]="getEditFormControl(featureFlag.id, 'name')!"
                class="w-full p-1 text-agentium_gray-text border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-momentum-deep_purple"
              />
              @if (getEditFormControl(featureFlag.id, "name")!.invalid) {
                <p class="text-red-500 mt-1">Name is required</p>
              }
            </div>
          }
        </div>
      </td>
    </ng-container>

    <!-- Description Column -->
    <ng-container cdkColumnDef="description">
      <th cdk-header-cell *cdkHeaderCellDef class="border-b p-2 min-w-[400px] text-agentium_gray-text">Description</th>
      <td cdk-cell *cdkCellDef="let featureFlag" class="border-b p-2 min-w-[400px]">
        @if (!featureFlag.editMode) {
          <span class="text-agentium_gray-text">{{ featureFlag.description }}</span>
        } @else {
          <textarea
            [formControl]="getEditFormControl(featureFlag.id, 'description')!"
            class="w-full p-1 text-agentium_gray-text border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-momentum-deep_purple"
            rows="2"
          ></textarea>
          @if (getEditFormControl(featureFlag.id, "description")!.invalid) {
            <p class="text-red-500 mt-1">Description is required</p>
          }
        }
      </td>
    </ng-container>

    <!-- Enabled Column -->
    <ng-container cdkColumnDef="enabled">
      <th cdk-header-cell *cdkHeaderCellDef class="border-b p-2 min-w-[120px] text-center text-agentium_gray-text">Enabled</th>
      <td cdk-cell *cdkCellDef="let featureFlag" class="border-b p-2 min-w-[120px] text-agentium_gray-text">
        <div class="h-full w-full flex justify-center items-center">
          @if (!featureFlag.editMode) {
            <div
              role="switch"
              class="relative inline-block w-10 h-6 rounded-full cursor-pointer"
              [attr.aria-checked]="featureFlag.enabled"
              tabindex="0"
              (click)="toggleEnabled(featureFlag)"
              (keydown.space)="toggleEnabled(featureFlag); $event.preventDefault()"
            >
              <div class="w-full h-full rounded-full transition-colors duration-200 ease-in-out" [ngClass]="featureFlag.enabled ? 'bg-momentum-deep_purple' : 'bg-gray-300'"></div>
              <div
                class="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transform transition-transform duration-200 ease-in-out"
                [ngClass]="featureFlag.enabled ? 'translate-x-4' : 'translate-x-0'"
              ></div>
            </div>
          }
          @if (this.dataSource.loadingMap[featureFlag.id]) {
            <fa-icon [icon]="faGear" animation="spin" class="pl-4 text-slate-600" size="lg"></fa-icon>
          }
        </div>
      </td>
    </ng-container>

    <!-- Actions Column -->
    <ng-container cdkColumnDef="actions">
      <th cdk-header-cell *cdkHeaderCellDef class="border-b p-2 min-w-[150px] text-center text-agentium_gray-text">Actions</th>
      <td cdk-cell *cdkCellDef="let featureFlag" class="border-b p-2 text-center min-w-[150px]">
        @if (!featureFlag.editMode) {
          <button type="button" (click)="toggleEdit(featureFlag)" class="px-3 py-1 bg-momentum-deep_purple enabled:text-white rounded-full disabled:bg-agentium_gray-text disabled:cursor-not-allowed">
            Edit
          </button>
        } @else {
          <div class="flex justify-center space-x-2">
            <button
              type="button"
              (click)="saveChanges(featureFlag)"
              [disabled]="editForms.get(featureFlag.id)?.invalid"
              class="px-3 py-1 bg-momentum-deep_purple enabled:text-white rounded-full flex items-center disabled:bg-agentium_gray-text disabled:cursor-not-allowed"
            >
              Save
            </button>
            <button
              type="button"
              (click)="cancelEdit(featureFlag)"
              class="px-3 py-1 bg-agentium_white-backdrop border border-gray-300 rounded-full disabled:bg-agentium_gray-text disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        }
      </td>
    </ng-container>

    <tr cdk-header-row *cdkHeaderRowDef="displayedColumns"></tr>
    <tr cdk-row *cdkRowDef="let row; columns: displayedColumns" class="hover:bg-gray-50 transition-colors"></tr>
  </table>

  <!-- Grid Loading Spinner -->
  @if (this.dataSource.isLoading) {
    <div class="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70">
      <fa-icon [icon]="faGear" animation="spin" class="p-4 text-slate-600" size="2xl"></fa-icon>
    </div>
  }

  <!-- No Data Message -->
  @if (!this.dataSource.isLoading && this.dataSource.data.length === 0) {
    <div class="flex flex-col items-center justify-center p-8 text-center">
      <p class="text-gray-500 mb-4">No feature flags found</p>
      <button
        type="button"
        (click)="toggleNewFeatureFlagForm()"
        class="px-3 py-1 bg-momentum-deep_purple text-white rounded hover:bg-momentum-royal_purple focus:outline-none focus:ring-2 focus:ring-momentum-deep_purple flex items-center"
      >
        <fa-icon [icon]="faPlus" class="mr-1"></fa-icon>
        Add First Flag
      </button>
    </div>
  }
</div>
